// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ConsentPreferences
 * @package @geenius/analytics/solidjs
 * @description Renders granular consent category controls for the SolidJS
 * analytics variant.
 */

import type { ConsentSettings } from "@geenius/analytics";
import { Button } from "@geenius/ui/solidjs";
import {
  type Component,
  createSignal,
  createUniqueId,
  For,
  Show,
} from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { cn } from "../lib/cn";
import { useAnalyticsConsent } from "../provider";

export type ConsentPreferenceScope =
  | "analytics"
  | "marketing"
  | "personalization";

export type ConsentPreferenceValues = Pick<
  ConsentSettings,
  ConsentPreferenceScope
>;

const CONSENT_PREFERENCE_OPTIONS = [
  ["personalization", "analytics.consent.personalization"],
  ["analytics", "analytics.consent.analyticsScope"],
  ["marketing", "analytics.consent.marketing"],
] as const satisfies readonly [ConsentPreferenceScope, string][];

export const DEFAULT_CONSENT_PREFERENCES: ConsentPreferenceValues = {
  analytics: false,
  marketing: false,
  personalization: false,
};

export function buildConsentSettings(
  preferences: ConsentPreferenceValues,
): ConsentSettings {
  return {
    essential: true,
    ...preferences,
    timestamp: Date.now(),
  };
}

function normalizeConsentPreferences(
  preferences?: Partial<ConsentPreferenceValues> | null,
): ConsentPreferenceValues {
  return {
    ...DEFAULT_CONSENT_PREFERENCES,
    ...preferences,
  };
}

/**
 * Props for rendering the granular SolidJS consent preferences control.
 *
 * @property value Optional controlled preference values.
 * @property onChange Called whenever a category toggle changes.
 * @property onSave Called when the user saves the current category choices.
 * @property showActions Whether to show the built-in save button.
 * @property class Optional class name merged into the fieldset.
 */
export interface ConsentPreferencesProps {
  value?: Partial<ConsentPreferenceValues>;
  onChange?: (preferences: ConsentPreferenceValues) => void;
  onSave?: (settings: ConsentSettings) => void;
  showActions?: boolean;
  class?: string;
}

/**
 * Renders category-level consent controls for analytics, marketing, and
 * personalization scopes.
 *
 * @param props Controlled values and callbacks for the consent controls.
 * @returns A fieldset containing category toggles and an optional save action.
 */
export const ConsentPreferences: Component<ConsentPreferencesProps> = (
  props,
) => {
  const copy = createAnalyticsCopy();
  const consent = useAnalyticsConsent();
  const legendId = createUniqueId();
  const [localPreferences, setLocalPreferences] =
    createSignal<ConsentPreferenceValues>(
      normalizeConsentPreferences(consent?.consentSettings()),
    );
  const preferences = () =>
    props.value !== undefined
      ? normalizeConsentPreferences(props.value)
      : localPreferences();

  const updatePreference = (
    scope: ConsentPreferenceScope,
    checked: boolean,
  ): void => {
    const next = {
      ...preferences(),
      [scope]: checked,
    };
    if (props.value === undefined) {
      setLocalPreferences(next);
    }
    props.onChange?.(next);
  };

  const savePreferences = (): void => {
    const settings = buildConsentSettings(preferences());
    consent?.updateConsent(settings);
    props.onSave?.(settings);
  };

  return (
    <fieldset
      class={cn(
        "grid gap-3 rounded-lg border border-border bg-background/70 p-3",
        props.class,
      )}
      data-analytics="consent-preferences"
      aria-labelledby={legendId}
    >
      <legend id={legendId} class="sr-only">
        {copy.t("analytics.consent.preferencesLegend")}
      </legend>
      <For each={CONSENT_PREFERENCE_OPTIONS}>
        {([scope, labelKey]) => (
          <label class="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm text-foreground">
            <span>{copy.t(labelKey)}</span>
            <input
              type="checkbox"
              class="h-4 w-4 rounded border-border text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              checked={preferences()[scope]}
              onChange={(event) =>
                updatePreference(scope, event.currentTarget.checked)
              }
            />
          </label>
        )}
      </For>
      <Show when={props.showActions !== false}>
        <div class="flex flex-wrap gap-3">
          <Button
            type="button"
            variant="secondary"
            data-analytics="consent-save"
            onClick={savePreferences}
          >
            {copy.t("analytics.consent.savePreferences")}
          </Button>
        </div>
      </Show>
    </fieldset>
  );
};
